import { useEffect, useState } from "react";
import { toast } from "sonner";

import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import {
  OppositionWorkflowStep,
  getOppositionProgress,
  recordCallClientForOpposition,
} from "api/opposition";

export const OPPOSITION_CALL_CLIENT_FORM_ID = "opposition-call-client-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionCallClientView({ leadId, onSuccess }: Props) {
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setRecorded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getOppositionProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === OppositionWorkflowStep.OP_S1_06_CallClientForOpposition
        );
        if (!cancelled && step) setRecorded(step.isCompleted || step.isSkipped);
      } catch {
        if (!cancelled) setRecorded(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (recorded) {
      onSuccess?.(leadId);
      return;
    }
    setRecording(true);
    try {
      await recordCallClientForOpposition(leadId);
      setRecorded(true);
      toast.success("Call Client step recorded successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record call client step"
      );
    } finally {
      setRecording(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_CALL_CLIENT_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div
        className="flex flex-row items-center gap-4 p-4"
        style={{
          background: "#FFF8EE",
          border: "1.5px dashed #F5A623",
          borderRadius: "12px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />

        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Call the client to explain the opposition and the need for a reply.
          </p>

          {recorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
              disabled={recording}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
